/**
 * Feature module identifiers. A module owns its collections, server
 * persistence and UI. Core modules cannot be disabled; optional modules can be
 * switched off per deployment with DISABLED_MODULES on the server, which tells
 * the web client at sign-in, without touching other modules.
 */
export const MODULE_IDS = [
  'calendar',
  'lists',
  'tasks',
  'meals',
  'photos',
  'vault',
  'pets',
  'maintenance',
  'goals',
  'emergency',
  'memories',
  'board',
  'comments',
] as const;

export type ModuleId = (typeof MODULE_IDS)[number];

export const CORE_MODULES: ReadonlySet<ModuleId> = new Set<ModuleId>(['calendar', 'lists', 'tasks', 'emergency', 'comments']);

export function isModuleId(value: string): value is ModuleId {
  return (MODULE_IDS as readonly string[]).includes(value);
}

/**
 * Resolve the enabled module set from a comma-separated "disabled" list.
 * Unknown ids and attempts to disable core modules are ignored (and reported
 * back so callers can log them).
 */
export function resolveEnabledModules(disabled: string | readonly string[] | undefined): {
  enabled: ReadonlySet<ModuleId>;
  ignored: string[];
} {
  const requested = (typeof disabled === 'string' ? disabled.split(',') : (disabled ?? [])).map((s) => s.trim()).filter(Boolean);
  const ignored: string[] = [];
  const off = new Set<ModuleId>();
  for (const id of requested) {
    if (!isModuleId(id) || CORE_MODULES.has(id)) ignored.push(id);
    else off.add(id);
  }
  return { enabled: new Set(MODULE_IDS.filter((id) => !off.has(id))), ignored };
}
